// Types
import { OptionResponse } from '@shared/types';

// Constants
import { MasterCategoryType } from '@shared/constants';

// Individual data types that map to grouped types
export type IndividualOptionDataType =
  | 'jobTitles'
  | 'technicalSkills'
  | 'technicalSkillCategories'
  | 'socialLinks'
  | 'degrees'
  | 'schools'
  | 'fieldsOfStudy'
  | 'certificationNames'
  | 'certificationOrganizations';

// Centralized mapping configuration
const TYPE_MAPPINGS: Partial<
  Record<MasterCategoryType, IndividualOptionDataType[]>
> = {
  [MasterCategoryType.SKILLS]: ['technicalSkills', 'technicalSkillCategories'],
  [MasterCategoryType.CERTIFICATIONS]: [
    'certificationNames',
    'certificationOrganizations',
  ],
} as const;

// Create reverse mapping for efficient lookup
const INDIVIDUAL_TO_GROUPED_MAP = new Map<
  IndividualOptionDataType,
  MasterCategoryType
>();

// Populate the reverse mapping
Object.entries(TYPE_MAPPINGS).forEach(([groupedType, individualTypes]) => {
  individualTypes.forEach((individualType) => {
    INDIVIDUAL_TO_GROUPED_MAP.set(
      individualType,
      groupedType as MasterCategoryType,
    );
  });
});

// Helper function to map grouped types to individual types
export const getIndividualTypesFromGrouped = (
  type: MasterCategoryType,
): IndividualOptionDataType[] =>
  TYPE_MAPPINGS[type] || [type as IndividualOptionDataType];

// Helper function to get grouped type from individual type
export const getGroupedTypeFromIndividual = (
  type: IndividualOptionDataType,
): MasterCategoryType =>
  INDIVIDUAL_TO_GROUPED_MAP.get(type) || (type as MasterCategoryType);

// Interface for the complete option data object
export interface OptionDataObject {
  technicalSkills: OptionResponse[];
  technicalSkillCategories: OptionResponse[];
  socialLinks: OptionResponse[];
  degrees: OptionResponse[];
  schools: OptionResponse[];
  jobTitles: OptionResponse[];
  fieldsOfStudy: OptionResponse[];
  certificationNames: OptionResponse[];
  certificationOrganizations: OptionResponse[];
  // Add index signature for dynamic access
  [key: string]: OptionResponse[];
}

// State interface
export interface OptionDataState {
  optionData: OptionDataObject | null;
  isOptionDataLoading: boolean;
  isOptionDataFetched: boolean;
  optionDataLoadingStates: Record<IndividualOptionDataType, boolean>;
  optionDataLastFetched: Record<IndividualOptionDataType, number>;
  optionDataErrors: Record<IndividualOptionDataType, string | null>;
}

// Actions interface
export interface OptionDataActions {
  setOptionData: (data: OptionDataObject | null) => void;
  setOptionDataLoading: (loading: boolean) => void;
  setOptionDataFetched: (fetched: boolean) => void;
  fetchOptionDataByTypes: (
    types: MasterCategoryType[],
    force?: boolean,
    token?: string,
  ) => Promise<void>;
  getOptionDataByType: (
    type: MasterCategoryType,
  ) => Partial<Record<IndividualOptionDataType, OptionResponse[]>>;
  shouldRefetchOptionData: (
    type: MasterCategoryType,
    cacheTimeMs?: number,
  ) => boolean;
  clearOptionDataByType: (type?: MasterCategoryType) => void;
  resetOptionData: () => void;
}

// Combined slice interface
export interface OptionDataSlice extends OptionDataState, OptionDataActions {}
